import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { 
  TrendingUp, DollarSign, Users, Package, Calendar,
  AlertTriangle, ChevronRight, Filter, MoreVertical, CreditCard
} from 'lucide-react';
import { 
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Cell
} from 'recharts';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';

const currency = (value) => `KES ${Number(value || 0).toLocaleString()}`;

const formatDate = (value) => {
  if (!value) return '';

  return new Date(value).toLocaleDateString('en-KE', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

const formatPaymentMethod = (value) => {
  if (!value) return 'Not recorded';

  return value
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const Dashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [salesPeriod, setSalesPeriod] = useState('week');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

  const fetchDashboardData = useCallback(async () => {
    try {
      const response = await api.get('/dashboard', {
        params: {
          period: salesPeriod,
          date: selectedDate
        }
      });
      setDashboardData(response.data);
      setLoading(false);
    } catch (error) {
      toast.error('Failed to load dashboard data');
      setLoading(false);
    }
  }, [salesPeriod, selectedDate]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const chartData = (dashboardData?.salesOverview || []).map((point) => ({
    day: point.label,
    sales: Number(point.total_sales || 0),
    transactions: Number(point.transactions || 0)
  }));

  const productColors = ['bg-brand-blue', 'bg-brand-green', 'bg-brand-purple', 'bg-brand-orange', 'bg-brand-pink'];
  const topProducts = (dashboardData?.topProducts || []).map((product, index) => ({
    name: product.name,
    quantity: Number(product.total_quantity || 0),
    revenue: Number(product.total_revenue || 0),
    color: productColors[index % productColors.length]
  }));

  const recentTransactions = (dashboardData?.recentTransactions || []).map((transaction) => ({
    id: transaction.receipt_number,
    client: transaction.client_name || 'Walk-in client',
    amount: Number(transaction.total_amount || 0),
    date: formatDate(transaction.sale_date),
    payment: formatPaymentMethod(transaction.payment_method),
    status: formatPaymentMethod(transaction.payment_status)
  }));

  const isToday = selectedDate === new Date().toISOString().split('T')[0];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="flex flex-col items-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-green"></div>
          <p className="text-gray-500">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const kpiCards = [
    {
      label: isToday ? "Today's Sales" : 'Selected Date Sales',
      value: currency(dashboardData?.todaySales?.amount),
      subtext: `${dashboardData?.todaySales?.transactions || 0} transactions`,
      icon: DollarSign,
      color: 'green',
      bgColor: 'bg-brand-green-50',
      iconBg: 'bg-brand-green-100',
      iconColor: 'text-brand-green',
    },
    {
      label: 'Monthly Sales',
      value: currency(dashboardData?.monthlyRevenue),
      subtext: 'Selected month',
      icon: TrendingUp,
      color: 'blue',
      bgColor: 'bg-brand-blue-50',
      iconBg: 'bg-brand-blue-100',
      iconColor: 'text-brand-blue',
    },
    {
      label: 'Total Clients',
      value: dashboardData?.totalClients || 0,
      subtext: `${dashboardData?.newClientsThisMonth || 0} new in selected month`,
      icon: Users,
      color: 'purple',
      bgColor: 'bg-brand-purple-50',
      iconBg: 'bg-brand-purple-100',
      iconColor: 'text-brand-purple',
    },
    {
      label: 'Total Products',
      value: dashboardData?.totalProducts || 0,
      subtext: `${dashboardData?.lowStockProducts || 0} low stock alerts`,
      icon: Package,
      color: 'orange',
      bgColor: 'bg-brand-orange-50',
      iconBg: 'bg-brand-orange-100',
      iconColor: 'text-brand-orange',
    },
  ];

  return (
    <div className="space-y-6 page-content">
      {/* Header Row */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 animate-fade-in-up">
        <div>
      <h1 className="text-2xl font-bold text-neutral-dark dark:text-gray-100 flex items-center gap-3">
            <span className="w-1 h-8 bg-gradient-to-b from-brand-green to-brand-blue rounded-full inline-block"></span>
            Dashboard
          </h1>
          <p className="text-sm text-neutral-gray dark:text-gray-400 mt-1 ml-4">Welcome back, here's your clinic overview</p>
        </div>
        
        {/* Date Selector */}
        <div className="flex items-center space-x-3">
          <div className="relative group">
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="pl-10 pr-4 py-2 border border-neutral-medium rounded-xl text-sm focus:ring-2 focus:ring-brand-green focus:border-brand-green bg-white shadow-sm transition-all group-hover:shadow-md"
            />
            <Calendar size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-gray" />
          </div>
          <button className="p-2 border border-neutral-medium rounded-xl hover:bg-neutral-light shadow-sm hover:shadow-md transition-all">
            <Filter size={16} className="text-neutral-gray" />
          </button>
        </div>
      </div>

      {/* KPI Cards Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {kpiCards.map((card, index) => {
          const Icon = card.icon;
          return (
            <div 
              key={index} 
              className="bg-white dark:bg-[#1e1e2e] rounded-xl shadow-sm border border-neutral-medium dark:border-[#2d2d44] p-5 card-hover animate-fade-in-up"
              style={{ animationDelay: `${0.1 + index * 0.1}s` }}
            >
              <div className="flex items-start justify-between mb-3">
                <p className="text-sm font-medium text-neutral-gray dark:text-gray-400">{card.label}</p>
                <div className={`p-2.5 rounded-xl ${card.iconBg} kpi-icon`}>
                  <Icon size={18} className={card.iconColor} />
                </div>
              </div>
              
              <p className="text-2xl font-bold text-neutral-dark mb-2">{card.value}</p>
              
              <p className="text-xs text-neutral-gray flex items-center gap-1">
                <span className={`w-1.5 h-1.5 rounded-full inline-block ${card.color === 'green' ? 'bg-brand-green' : card.color === 'blue' ? 'bg-brand-blue' : card.color === 'purple' ? 'bg-brand-purple' : 'bg-brand-orange'}`}></span>
                {card.subtext}
              </p>
            </div>
          );
        })}
      </div>

      {/* Second Row - Analytics Section */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* Sales Overview Chart - 50% width */}
        <div className="lg:col-span-2 bg-white dark:bg-[#1e1e2e] rounded-xl shadow-sm border border-neutral-medium dark:border-[#2d2d44] p-6 card-hover animate-fade-in-up anim-delay-1">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-semibold text-neutral-dark dark:text-gray-100 flex items-center gap-2">
                <TrendingUp size={18} className="text-brand-green" />
                Sales Overview
              </h3>
              <p className="text-sm text-neutral-gray mt-1">Recorded sales for the selected period</p>
            </div>
            <select
              value={salesPeriod}
              onChange={(e) => setSalesPeriod(e.target.value)}
              className="px-3 py-1.5 border border-neutral-medium rounded-lg text-sm focus:ring-2 focus:ring-brand-green bg-white shadow-sm"
            >
              <option value="week">This Week</option>
              <option value="month">This Month</option>
              <option value="year">This Year</option>
            </select>
          </div>
          
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis 
                  dataKey="day"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#6B7280', fontSize: 12 }}
                />
                <YAxis 
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#6B7280', fontSize: 12 }}
                  tickFormatter={(value) => `KES ${(value / 1000).toFixed(0)}k`}
                />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: 'white',
                    border: 'none',
                    borderRadius: '12px',
                    boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
                    padding: '12px'
                  }}
                  formatter={(value) => [`KES ${value.toLocaleString()}`, 'Sales']}
                />
                <Bar 
                  dataKey="sales"
                  fill="#4CAF50"
                  radius={[6, 6, 0, 0]}
                  barSize={24}
                >
                  {chartData.map((entry, idx) => (
                    <Cell key={`cell-${idx}`} fill={idx % 2 === 0 ? '#4CAF50' : '#4A90E2'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Top Selling Products - 25% width */}
        <div className="lg:col-span-1 bg-white rounded-xl shadow-sm border border-neutral-medium p-6 card-hover animate-fade-in-up anim-delay-2">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-neutral-dark flex items-center gap-2">
              <Package size={18} className="text-brand-purple" />
              Top Products
            </h3>
          </div>
          
          <div className="space-y-4">
            {topProducts.length === 0 ? (
              <p className="text-sm text-neutral-gray">No recorded product sales.</p>
            ) : topProducts.slice(0, 5).map((product, index) => (
              <div key={index} className="flex items-center justify-between p-2 rounded-lg hover:bg-neutral-light transition-colors">
                <div className="flex items-center space-x-3 min-w-0">
                  <div className={`w-3 h-3 rounded-full flex-shrink-0 ${product.color}`}></div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-neutral-dark truncate">{product.name}</p>
                    <p className="text-xs text-neutral-gray">{currency(product.revenue)}</p>
                  </div>
                </div>
                <span className="text-sm font-semibold text-neutral-dark ml-3 bg-neutral-light px-2 py-0.5 rounded-lg">{product.quantity}</span>
              </div>
            ))}
          </div>
          
          <Link to="/inventory" className="flex items-center justify-center mt-6 text-sm text-brand-green hover:text-brand-green-700 font-medium bg-brand-green-50 hover:bg-brand-green-100 py-2 rounded-lg transition-colors">
            View All Products
            <ChevronRight size={16} className="ml-1" />
          </Link>
        </div>

        {/* Recent Transactions - 25% width */}
        <div className="lg:col-span-1 bg-white rounded-xl shadow-sm border border-neutral-medium p-6 card-hover animate-fade-in-up anim-delay-3">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-neutral-dark flex items-center gap-2">
              <DollarSign size={18} className="text-brand-blue" />
              Recent Transactions
            </h3>
            <Link to="/finance" className="text-sm text-brand-green hover:text-brand-green-700 font-medium">
              View All
            </Link>
          </div>
          
          <div className="space-y-1">
            {recentTransactions.length === 0 ? (
              <p className="text-sm text-neutral-gray">No recorded transactions.</p>
            ) : recentTransactions.slice(0, 5).map((transaction, index) => (
              <Link
                key={index}
                to={`/finance/receipt/${encodeURIComponent(transaction.id)}`}
                className="block"
              >
                <div className="flex items-center justify-between p-2 rounded-lg border-b border-neutral-medium last:border-0 hover:bg-neutral-light transition-colors">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-neutral-dark truncate">{transaction.id}</p>
                    <p className="text-xs text-neutral-gray truncate">{transaction.client}</p>
                    <p className="text-[11px] text-gray-400">{transaction.payment} · {transaction.status}</p>
                  </div>
                  <div className="text-right ml-3">
                    <p className="text-sm font-semibold text-neutral-dark">
                      {currency(transaction.amount)}
                    </p>
                    <p className="text-xs text-neutral-gray">{transaction.date}</p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom Row - Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Low Stock Alerts */}
        <div className="bg-gradient-to-br from-brand-red-50 to-brand-red-100/50 rounded-xl p-5 border border-brand-red-100 relative overflow-hidden card-hover animate-fade-in-up">
          <div className="absolute top-0 right-0 w-32 h-32 bg-brand-red-100 rounded-full -mr-10 -mt-10 opacity-60 blur-xl"></div>
          <div className="relative">
            <div className="flex items-center justify-between mb-3">
              <div className="p-2.5 bg-brand-red-100 rounded-xl">
                <AlertTriangle size={20} className="text-brand-red" />
              </div>
              <Link to="/inventory" className="text-sm text-brand-red hover:text-brand-red-700 font-medium flex items-center gap-1 bg-white/60 px-3 py-1 rounded-lg hover:bg-white/90 transition-colors">
                View <ChevronRight size={16} />
              </Link>
            </div>
            <p className="text-sm font-medium text-brand-red-700 mb-1">Low Stock Alerts</p>
            <p className="text-3xl font-bold text-brand-red-800">{dashboardData?.lowStockProducts || 0}</p>
            <p className="text-sm text-brand-red mt-1">Products need reordering</p>
          </div>
        </div>

        {/* Upcoming Appointments */}
        <div className="bg-gradient-to-br from-brand-blue-50 to-brand-blue-100/50 rounded-xl p-5 border border-brand-blue-100 relative overflow-hidden card-hover animate-fade-in-up anim-delay-1">
          <div className="absolute top-0 right-0 w-32 h-32 bg-brand-blue-100 rounded-full -mr-10 -mt-10 opacity-60 blur-xl"></div>
          <div className="relative">
            <div className="flex items-center justify-between mb-3">
              <div className="p-2.5 bg-brand-blue-100 rounded-xl">
                <Calendar size={20} className="text-brand-blue" />
              </div>
              <Link to="/appointments" className="text-sm text-brand-blue hover:text-brand-blue-700 font-medium flex items-center gap-1 bg-white/60 px-3 py-1 rounded-lg hover:bg-white/90 transition-colors">
                View <ChevronRight size={16} />
              </Link>
            </div>
            <p className="text-sm font-medium text-brand-blue-700 mb-1">Upcoming Appointments</p>
            <p className="text-3xl font-bold text-brand-blue-800">{dashboardData?.upcomingAppointments || 0}</p>
            <p className="text-sm text-brand-blue mt-1">Scheduled from selected date</p>
          </div>
        </div>

        {/* Outstanding Payments */}
        <div className="bg-gradient-to-br from-brand-orange-50 to-brand-orange-100/50 rounded-xl p-5 border border-brand-orange-100 relative overflow-hidden card-hover animate-fade-in-up anim-delay-2">
          <div className="absolute top-0 right-0 w-32 h-32 bg-brand-orange-100 rounded-full -mr-10 -mt-10 opacity-60 blur-xl"></div>
          <div className="relative">
            <div className="flex items-center justify-between mb-3">
              <div className="p-2.5 bg-brand-orange-100 rounded-xl">
                <CreditCard size={20} className="text-brand-orange" />
              </div>
              <Link to="/finance" className="text-sm text-brand-orange hover:text-brand-orange-700 font-medium flex items-center gap-1 bg-white/60 px-3 py-1 rounded-lg hover:bg-white/90 transition-colors">
                View <ChevronRight size={16} />
              </Link>
            </div>
            <p className="text-sm font-medium text-brand-orange-700 mb-1">Outstanding Payments</p>
            <p className="text-3xl font-bold text-brand-orange-800">
              {currency(dashboardData?.outstandingPayments?.amount)}
            </p>
            <p className="text-sm text-brand-orange mt-1">{dashboardData?.outstandingPayments?.count || 0} pending invoices</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;